import { useRef, useState } from "react";
import { Loader2, Upload, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const readDuration = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
  v.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read this video")); };
  v.src = url;
});

// Apply with a ≤15-second video: duration checked in the browser, uploaded via the existing /uploads endpoint, then submitted.
export default function TopBadgeApply({ maxSeconds = 15, onSubmitted }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Please choose a video file (MP4 or WebM).");
    try {
      const d = await readDuration(f);
      if (d > maxSeconds + 0.5) { setFile(null); setDuration(null); return toast.error(`That video is ${d.toFixed(1)}s — the limit is ${maxSeconds} seconds.`); }
      setFile(f); setDuration(d);
    } catch (err) { toast.error(err.message); }
  };

  const submit = async () => {
    if (!file || duration == null) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/top-badge/entries", { file_id: up.id, duration_s: duration, caption: caption.trim() || null });
      toast.success("You're in! Your video is live on the campaign page.");
      setFile(null); setDuration(null); setCaption("");
      onSubmitted?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit your entry."); }
    finally { setBusy(false); }
  };

  return (
    <section className="card-surface p-5 space-y-3" data-testid="top-badge-apply">
      <div className="flex items-center gap-2 text-white"><Video size={16} className="text-yellow-300" /> Apply with a {maxSeconds}-second video</div>
      <p className="text-xs text-zinc-400">Tell members why you deserve the Top Badge. One entry per member. MP4 or WebM, up to 25 MB, {maxSeconds} seconds max.</p>
      <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/*" className="hidden" onChange={pick} data-testid="top-badge-video-input" />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="top-badge-pick-video"><Upload size={14} className="mr-1" /> {file ? "Change video" : "Choose video"}</Button>
        {file && <span className="text-xs text-zinc-300" data-testid="top-badge-video-meta">{file.name} · {duration?.toFixed(1)}s</span>}
      </div>
      <Input value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 140))} placeholder="One line about you (optional, 140 chars)" data-testid="top-badge-caption-input" />
      <Button onClick={submit} disabled={!file || busy} className="bg-yellow-400 text-black hover:bg-yellow-300" data-testid="top-badge-submit-entry">
        {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Submit my entry
      </Button>
    </section>
  );
}
